import type { Aabb, District, InteriorTemplate, Plot, SpawnAnchor, Vec2, WorldManifest } from "@townsy/contracts";
import { interiorWallColliders, pointInsideAny } from "./collision";
import { distance } from "./geometry";
import { WORLD_MANIFEST } from "./manifest";
import type { ColliderSet } from "./spatialIndex";
import { streetDecor, streetDecorColliders } from "./streetDecor";

export function getDistrict(manifest: WorldManifest, districtId: string): District {
  const d = manifest.districts.find((x) => x.id === districtId);
  if (!d) throw new Error(`unknown district ${districtId}`);
  return d;
}

export function getPlot(manifest: WorldManifest, plotId: string): Plot | undefined {
  return manifest.plots.find((p) => p.id === plotId);
}

export function getInterior(manifest: WorldManifest, id: string): InteriorTemplate {
  const t = manifest.interiors.find((x) => x.id === id);
  if (!t) throw new Error(`unknown interior template ${id}`);
  return t;
}

/** The collider of an OWNED home (the max home footprint, so upgrades never trap anyone). */
export function homeCollider(plot: Plot): Aabb {
  return plot.buildBounds;
}

/**
 * Collider boxes for the street: every static collider, one box per OWNED residential plot, and
 * every solid street decor piece (streetDecor: hedges, mailboxes, For Sale signs, lot corners,
 * billboard legs, civic columns/posts, awning posts, window boxes; ADR-0006). Unowned residential
 * plots are open land with a sign and corner hedges. The server and the client's prediction MUST pass
 * the same owned set (derived from PropertyPublic.status) and fenced set (fencedPlotIds of the yard
 * placements: a front fence replaces the hedges) or prediction drifts. Add yard items with
 * yardDecorationColliders and cars as oriented obstacles (stepMotion `obstacles`).
 * World v6 (ADR-0042): handed the WHOLE manifest these are the colliders of the whole town (its one street
 * room: every district's buildings, props, walls with the openings for Market Street, decor and owned
 * homes; townColliders, town.ts, is the same call from a slice or the whole manifest). Handed a district
 * slice (districtManifest(m, id)) they are that district's alone (v5: the whole manifest meant Central).
 */
export function streetColliders(
  manifest: WorldManifest,
  ownedResidentialIds: ReadonlySet<string> = new Set(),
  fencedPlotIds: ReadonlySet<string> = new Set(),
): Aabb[] {
  const street = manifest;
  const boxes = street.colliders.map((c) => c.aabb);
  if (ownedResidentialIds.size) {
    for (const p of street.plots) {
      if (p.type === "RESIDENTIAL" && ownedResidentialIds.has(p.id)) boxes.push(homeCollider(p));
    }
  }
  boxes.push(...streetDecorColliders(streetDecor(street, ownedResidentialIds, fencedPlotIds)));
  return boxes;
}

/** Collider boxes for an interior: its furniture plus a thin wall ring around the floor bounds. */
export function interiorColliders(t: InteriorTemplate): Aabb[] {
  return [...t.colliders.map((c) => c.aabb), ...interiorWallColliders(t)];
}

/**
 * Pick a free spawn point: the preferred anchor if nothing occupies it, otherwise the first
 * return anchor that is free of colliders and other actors; falls back to the preferred anchor.
 */
export function pickFreeSpawn(
  preferred: SpawnAnchor,
  alternatives: readonly SpawnAnchor[],
  colliders: ColliderSet,
  occupied: readonly Vec2[],
  radius: number,
): SpawnAnchor {
  const free = (a: SpawnAnchor) =>
    !pointInsideAny(a.position.x, a.position.z, radius, colliders) &&
    !occupied.some((o) => distance(o, a.position) < radius * 2.2);
  if (free(preferred)) return preferred;
  for (const a of alternatives) if (free(a)) return a;
  // Spiral around the preferred anchor as a last resort.
  for (let ring = 1; ring <= 6; ring++) {
    for (let k = 0; k < 8; k++) {
      const ang = (k / 8) * Math.PI * 2;
      const cand: SpawnAnchor = {
        position: { x: preferred.position.x + Math.cos(ang) * ring, z: preferred.position.z + Math.sin(ang) * ring },
        facing: preferred.facing,
      };
      if (free(cand)) return cand;
    }
  }
  return preferred;
}

/**
 * The commercial/residential plot whose entrance is within `radius` of a point (nearest wins). Works on the whole
 * manifest (world v5: the districts never overlap, so a point is near one district's entrances only) or a slice.
 */
export function nearestEntrance(manifest: WorldManifest, pos: Vec2, radius: number): Plot | null {
  let best: Plot | null = null;
  let bestD = Infinity;
  for (const p of manifest.plots) {
    const d = distance(pos, p.entrance.position);
    if (d <= radius && d < bestD) {
      best = p;
      bestD = d;
    }
  }
  return best;
}

export const DEFAULT_MANIFEST = WORLD_MANIFEST;
